import { DestroyRef, Injectable, inject, signal } from '@angular/core';

import { ChannelConfigService } from './channel-config.service';

export const BUY_NOW_COUNTDOWN_SECONDS = 5;

@Injectable({ providedIn: 'root' })
export class BuyNowRedirectService {
  private channelConfig = inject(ChannelConfigService);

  private pendingUrl: string | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  private readonly visibleSignal = signal(false);
  private readonly remainingSecondsSignal = signal(BUY_NOW_COUNTDOWN_SECONDS);

  readonly isVisible = this.visibleSignal.asReadonly();
  readonly remainingSeconds = this.remainingSecondsSignal.asReadonly();
  readonly totalSeconds = BUY_NOW_COUNTDOWN_SECONDS;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearCountdown());
  }

  open(url: string): void {
    const target = url?.trim();
    if (!target) {
      return;
    }

    if (!this.channelConfig.hasMovelCatalog) {
      this.redirect(target);
      return;
    }

    if (this.visibleSignal()) {
      return;
    }

    this.pendingUrl = target;
    this.remainingSecondsSignal.set(BUY_NOW_COUNTDOWN_SECONDS);
    this.visibleSignal.set(true);
    this.startCountdown();
  }

  continueNow(): void {
    const url = this.pendingUrl;
    if (!url) {
      return;
    }

    const tab = this.redirect(url);
    if (tab) {
      this.finishCountdown();
    }
  }

  dismiss(): void {
    if (!this.visibleSignal()) {
      return;
    }
    this.finishCountdown();
  }

  private startCountdown(): void {
    this.clearCountdown();
    this.countdownTimer = setInterval(() => {
      const next = this.remainingSecondsSignal() - 1;
      if (next <= 0) {
        this.remainingSecondsSignal.set(0);
        this.clearCountdown();
        const url = this.pendingUrl;
        if (!url) {
          return;
        }

        const tab = this.redirect(url);
        if (tab) {
          this.finishCountdown();
        }
        return;
      }
      this.remainingSecondsSignal.set(next);
    }, 1000);
  }

  private finishCountdown(): void {
    this.clearCountdown();
    this.pendingUrl = null;
    this.visibleSignal.set(false);
    this.remainingSecondsSignal.set(BUY_NOW_COUNTDOWN_SECONDS);
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private redirect(url: string): Window | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.open(url, '_blank', 'noopener,noreferrer');
  }
}
